"use client";

import { useDatadog } from "@auth/client-sdk-react";
import { usePostHog } from "posthog-js/react";
import {
  ExpressCheckoutElement,
  ExpressCheckoutElementProps,
} from "@stripe/react-stripe-js";
import type {
  StripeExpressCheckoutElementReadyEvent,
  StripeError,
} from "@stripe/stripe-js";

import {
  FRESH_PROCESSING_PHRASES,
} from "apps/website/components/feature/Checkout/shared/constants";
import ProcessingPaymentModal from "apps/website/components/feature/Modal/ProcessingPaymentModal/ProcessingPaymentModal";

import { CreateBasketResult } from "./types";
import { useConfirmPayment } from "./useConfirmPayment";

/** Inner component rendered inside the `<Elements>` provider so that
 * `useConfirmPayment` (which calls `useStripe` / `useElements`) can resolve
 * the Stripe & Elements instances from context.
 *
 * `createBasket` is injected from the version-specific wrapper so this
 * component (and `useConfirmPayment`) stays agnostic of v1 vs v2. */
export function PayPalExpressCheckoutInner({
  createBasket,
  onSuccess,
  onError,
  onAvailabilityChange,
}: Readonly<{
  createBasket: () => Promise<CreateBasketResult>;
  onSuccess: () => void;
  onError: (message: string) => void;
  onAvailabilityChange?: (available: boolean) => void;
}>) {
  const { handleConfirm, isProcessing } = useConfirmPayment({ createBasket, onSuccess, onError });
  const datadog = useDatadog();
  const posthog = usePostHog();

  // The PayPal-flavored analogue of `pr.canMakePayment()` (TECH-2814's
  // Express Checkout check): `availablePaymentMethods.paypal` is undefined
  // until Stripe finishes probing eligibility, then tells us whether the
  // PayPal button will actually render.
  const handleReady = (event: StripeExpressCheckoutElementReadyEvent) => {
    const isPaypalAvailable = Boolean(event.availablePaymentMethods?.paypal);

    // Only emit telemetry for consumers that wired up availability reporting
    // (i.e. TECH-2814's Fresh checkout). Other callers of this shared
    // component (e.g. the specialist consultation flow's
    // FormPayPalExpressCheckout) don't pass onAvailabilityChange and
    // shouldn't pollute this metric with an unrelated flow's data.
    if (!isPaypalAvailable && onAvailabilityChange) {
      datadog.logger.warn("No data returned for PayPal express checkout support check", {
        eventName: "ExpressCheckoutPaypalSupportNoData",
        availablePaymentMethods: event.availablePaymentMethods,
      });
      posthog.capture("PaypalSupportNoData", {
        browser: navigator.userAgent,
        device: /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop",
      });
    }

    onAvailabilityChange?.(isPaypalAvailable);
  };

  const handleLoadError = (event: { elementType: "expressCheckout"; error: StripeError }) => {
    if (onAvailabilityChange) {
      datadog.logger.error("Could not check for PayPal express checkout support", {
        eventName: "ExpressCheckoutPaypalSupportFailed",
        error: event.error,
      });
    }
    onAvailabilityChange?.(false);
  };

  const expressCheckoutOptions: ExpressCheckoutElementProps = {
    onConfirm: handleConfirm,
    onReady: handleReady,
    onLoadError: handleLoadError,
    options: {
      billingAddressRequired: false,
      // Restrict visible express buttons to PayPal only. Apple/Google Pay and
      // Link are explicitly disabled even though the parent `<Elements>` runs
      // in automatic mode and would otherwise enumerate them.
      paymentMethods: {
        applePay: "never",
        googlePay: "never",
        link: "never",
        paypal: "auto",
      },
    },
  };

  return (
    <>
      <ExpressCheckoutElement {...expressCheckoutOptions} />
      { isProcessing && (
        <ProcessingPaymentModal
          phrases={ FRESH_PROCESSING_PHRASES }
          processingComplete={ false }
        />
      ) }
    </>
  );
}
